import {
  Avatar,
  Button,
  Divider,
  DropdownButton,
  DropdownButtonContext,
  FlexBox,
  Icon,
  IconButton,
  SelectionList,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { ReadSearchApiArg, SearchFilterCondition } from 'lib-search-types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { rolesApi } from 'apis/roles';
import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { getUserDisplayName } from 'utils/user';

import {
  FilterDropdownTitle,
  FilterSearchInput,
  LoadingItem,
} from './components';
import styles from './searchControls.module.scss';
import { hasActiveFilters } from './utils';

export const statuses = [
  { label: 'Working agents', value: 'working' },
  { label: 'Away agents', value: 'away' },
  { label: 'Online agents', value: 'online' },
  { label: 'Offline agents', value: 'offline' },
  { label: 'Wrap-up agents', value: 'wrap-up' },
  { label: 'Favorite agents', value: 'favorite' },
  { label: 'Assigned agents', value: 'assigned' },
  { label: 'Unassigned agents', value: 'unassigned' },
] as const;

export type Status = (typeof statuses)[number]['value'];

export const VALID_STATUSES: readonly Status[] = statuses.map((s) => s.value);

export type ActiveFilters = {
  status: Status | null;
  team: string[];
  agent: string[];
  role: string[];
};

type SearchControlsProps = {
  activeFilters: ActiveFilters;
  setActiveFilters: Dispatch<SetStateAction<ActiveFilters>>;
  onRefreshMembers: () => void;
  isRefreshing: boolean;
};

export const SearchControls = ({
  activeFilters,
  setActiveFilters,
  onRefreshMembers,
  isRefreshing,
}: SearchControlsProps): JSX.Element => {
  const [roleOpened, setRoleOpened] = useState<boolean>(false);
  const [teamSearchQuery, setTeamSearchQuery] = useState<string>('');
  const [agentSearchQuery, setAgentSearchQuery] = useState<string>('');

  const teamSearchArg: ReadSearchApiArg<'teams'> = useMemo(
    () => ({
      scope: 'teams',
      query: teamSearchQuery || undefined,
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'is_deactivated',
            operator: 'not_equals',
            values: [{ value: 'true' }],
          },
        ],
      },
      sortBy: { field: 'name', order: 'asc' },
    }),
    [teamSearchQuery],
  );

  const agentSearchArg = useMemo(
    (): ReadSearchApiArg<'users'> => ({
      scope: 'users',
      query: agentSearchQuery || undefined,
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'user_type',
            operator: 'is_one',
            values: [{ value: 'member' }],
          },
          ...(activeFilters.team.length > 0
            ? ([
                {
                  field: 'teams',
                  operator: 'includes_one',
                  values: activeFilters.team.map((teamId) => ({
                    value: teamId,
                  })),
                },
              ] satisfies SearchFilterCondition<'users'>[])
            : []),
        ],
      },
      sortBy: { field: 'display_name', order: 'asc' },
    }),
    [agentSearchQuery, activeFilters.team],
  );

  const [readSearchTeams, { results: teams, ...teamsQueryResult }] =
    useLazyReadSearchInfiniteQuery<'teams'>();

  const [readSearchAgents, { results: agents, ...agentsQueryResult }] =
    useLazyReadSearchInfiniteQuery<'users'>();

  useUpdateEffect(() => {
    readSearchTeams(teamSearchArg, { debounceTime: 300 });
  }, [readSearchTeams, teamSearchArg, teamSearchQuery]);

  useUpdateEffect(() => {
    readSearchAgents(agentSearchArg, { debounceTime: 300 });
  }, [readSearchAgents, agentSearchArg]);

  const handleTeamDropdownToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        readSearchTeams(teamSearchArg);
      } else {
        setTeamSearchQuery('');
      }
    },
    [readSearchTeams, teamSearchArg],
  );

  const handleAgentDropdownToggle = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        readSearchAgents(agentSearchArg);
      } else {
        setAgentSearchQuery('');
      }
    },
    [readSearchAgents, agentSearchArg],
  );

  // Roles query
  const { systemRoles, customRoles, rolesLoading } = rolesApi.useReadRolesQuery(
    undefined,
    {
      skip: !roleOpened,
      selectFromResult: ({ data, isFetching }) => {
        const roles = data?.data.roles ?? [];
        return {
          systemRoles: roles
            .filter((role) => role.is_system_role)
            .sort((a, b) => a.name.localeCompare(b.name)),
          customRoles: roles
            .filter((role) => !role.is_system_role)
            .sort((a, b) => a.name.localeCompare(b.name)),
          rolesLoading: isFetching,
        };
      },
    },
  );

  const handleClearFilters = useCallback(() => {
    setActiveFilters({ status: null, team: [], agent: [], role: [] });
  }, [setActiveFilters]);

  return (
    <>
      {hasActiveFilters(activeFilters) && (
        <Button variant="tertiary" icon="close" onClick={handleClearFilters}>
          Clear filters
        </Button>
      )}

      <DropdownButton
        buttonProps={{
          variant: 'tertiary',
        }}
        dropdown={
          <DropdownButtonContext.Consumer>
            {({ toggleDropdown }) => (
              <div className={styles.dropdownScroll}>
                <SelectionList
                  isMultiple={false}
                  selectedValues={
                    activeFilters.status ? [activeFilters.status] : []
                  }
                  onChange={(e) => {
                    const newStatus = e.selectedValues[0] as Status | undefined;
                    setActiveFilters((prev) => ({
                      ...prev,
                      status:
                        newStatus === prev.status ? null : (newStatus ?? null),
                    }));
                    toggleDropdown();
                  }}
                >
                  <FilterDropdownTitle>Filter by status</FilterDropdownTitle>

                  {statuses.map((status) => (
                    <SelectionList.Item
                      key={status.value}
                      value={status.value}
                      contentClassName={styles.itemContent}
                    >
                      {status.label}
                    </SelectionList.Item>
                  ))}
                </SelectionList>
              </div>
            )}
          </DropdownButtonContext.Consumer>
        }
      >
        {activeFilters.status
          ? statuses.find((s) => s.value === activeFilters.status)?.label
          : 'Status'}
      </DropdownButton>

      <DropdownButton
        buttonProps={{
          variant: 'tertiary',
          label:
            activeFilters.team.length > 0
              ? `${activeFilters.team.length}`
              : undefined,
        }}
        destroyOnClose
        onToggle={handleTeamDropdownToggle}
        dropdown={
          <div className={styles.dropdownScroll}>
            <FilterDropdownTitle>Filter by team</FilterDropdownTitle>

            <FilterSearchInput
              value={teamSearchQuery}
              onChange={setTeamSearchQuery}
              placeholder="Search teams"
            />

            <SelectionList
              selectedValues={activeFilters.team}
              isMultiple
              onChange={(e) =>
                setActiveFilters((prev) => ({
                  ...prev,
                  team: e.selectedValues,
                }))
              }
            >
              {teams.map((team) => (
                <SelectionList.Item
                  key={team.id}
                  value={team.id}
                  contentClassName={styles.itemContent}
                >
                  {team.name}
                </SelectionList.Item>
              ))}

              {teamsQueryResult.isFetching && <LoadingItem />}

              {teamsQueryResult.hasNextPage && !teamsQueryResult.isFetching && (
                <Waypoint onEnter={teamsQueryResult.fetchNextPage} />
              )}
            </SelectionList>
          </div>
        }
      >
        Team
      </DropdownButton>

      <DropdownButton
        buttonProps={{
          variant: 'tertiary',
          label:
            activeFilters.role.length > 0
              ? `${activeFilters.role.length}`
              : undefined,
        }}
        onToggle={setRoleOpened}
        dropdown={
          <div className={styles.dropdownScroll}>
            <FilterDropdownTitle>Filter by role</FilterDropdownTitle>
            <SelectionList
              selectedValues={activeFilters.role}
              isMultiple
              onChange={(e) =>
                setActiveFilters((prev) => ({
                  ...prev,
                  role: e.selectedValues,
                }))
              }
            >
              {rolesLoading ? (
                <LoadingItem />
              ) : (
                <>
                  {systemRoles.map((role) => (
                    <SelectionList.Item
                      key={role.id}
                      value={role.id}
                      contentClassName={styles.itemContent}
                    >
                      <Icon
                        name="role-system"
                        color={typography.color.tertiary}
                        size={16}
                      />
                      {role.name}
                    </SelectionList.Item>
                  ))}

                  {systemRoles.length > 0 && customRoles.length > 0 && (
                    <Divider />
                  )}

                  {customRoles.map((role) => (
                    <SelectionList.Item
                      key={role.id}
                      value={role.id}
                      contentClassName={styles.itemContent}
                    >
                      <Icon
                        name="role-custom"
                        color={typography.color.primary}
                        size={16}
                      />
                      {role.name}
                    </SelectionList.Item>
                  ))}
                </>
              )}
            </SelectionList>
          </div>
        }
      >
        <FlexBox alignItems="center" gap="100">
          Role
        </FlexBox>
      </DropdownButton>

      <DropdownButton
        data-testid="agent-filter"
        buttonProps={{
          variant: 'tertiary',
          label:
            activeFilters.agent.length > 0
              ? `${activeFilters.agent.length}`
              : undefined,
        }}
        destroyOnClose
        onToggle={handleAgentDropdownToggle}
        dropdown={
          <div className={styles.dropdownScroll}>
            <FilterDropdownTitle>Filter by agent</FilterDropdownTitle>

            <FilterSearchInput
              value={agentSearchQuery}
              onChange={setAgentSearchQuery}
              placeholder="Search agents"
              data-testid="agent-filter-search"
            />

            <SelectionList
              selectedValues={activeFilters.agent}
              isMultiple
              onChange={(e) =>
                setActiveFilters((prev) => ({
                  ...prev,
                  agent: e.selectedValues,
                }))
              }
            >
              {agents.map((agent) => (
                <SelectionList.Item
                  key={agent.id}
                  value={agent.id}
                  contentClassName={styles.itemContent}
                >
                  <Avatar user={agent} size="24" />
                  <span>{getUserDisplayName(agent)}</span>
                </SelectionList.Item>
              ))}

              {agentsQueryResult.isFetching && <LoadingItem />}

              {agentsQueryResult.hasNextPage &&
                !agentsQueryResult.isFetching && (
                  <Waypoint onEnter={agentsQueryResult.fetchNextPage} />
                )}
            </SelectionList>
          </div>
        }
      >
        Agent
      </DropdownButton>

      <Tooltip content="Refresh">
        <IconButton
          icon="sync"
          variant="tertiary"
          isLoading={isRefreshing}
          onClick={onRefreshMembers}
        />
      </Tooltip>
    </>
  );
};
